import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useBrandingColors } from '../hooks/useBrandingColors';
import { useSidebarState } from '../hooks/useSidebarState';
import { useSelectedVacancyRouteSync } from '../hooks/useSelectedVacancyRouteSync';
import { Sidebar } from './Sidebar/Sidebar';
import { PageHeader } from './Header/PageHeader';
import { MainTabNavigation } from './Navigation/MainTabNavigation';
import ComplaintModal from './ComplaintModal';
import portfolioIcon from '../assets/icons/portfolio.png';
import { isAtsAvailable, isRelevanterAvailable } from '../ats/atsConfig';
import { useBrandingStore } from '../store/brandingStore';

const Layout: React.FC = () => {
	const { isAuth, isLoading } = useAuthStore();
	const colors = useBrandingColors();
	const navigate = useNavigate();
	const location = useLocation();
	const { companyData, loadCompanyData } = useBrandingStore();
	const { isCollapsed, toggle } = useSidebarState();
	const [isComplaintOpen, setComplaintOpen] = React.useState(false);
	const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
	useSelectedVacancyRouteSync();

	const path = location.pathname || '';
	const isHhResponsesPage = path.startsWith('/recruiter/hh-responses');
	const isAtsRoute = path === '/ats' || path.startsWith('/ats/');
	const isUtilityRoute =
		path.startsWith('/recruiter/account') ||
		path.startsWith('/recruiter/control') ||
		path.startsWith('/recruiter/branding') ||
		path.startsWith('/recruiter/learn');

	React.useEffect(() => {
		if (!companyData) {
			loadCompanyData().catch(() => {});
		}
	}, [companyData, loadCompanyData]);

	React.useEffect(() => {
		if (
			companyData &&
			!isRelevanterAvailable(companyData) &&
			isAtsAvailable(companyData) &&
			!isAtsRoute &&
			!isUtilityRoute
		) {
			navigate('/ats', { replace: true });
		}
	}, [companyData, isAtsRoute, isUtilityRoute, navigate, path]);

	React.useEffect(() => {
		if (path.startsWith('/recruiter/vacancy')) {
			const container = scrollContainerRef.current;
			if (container) {
				container.scrollTop = 0;
			}
		}
	}, [path]);

	// Обработчик глобального события для открытия модального окна жалоб
	React.useEffect(() => {
		const handleOpenComplaintModal = () => {
			setComplaintOpen(true);
		};

		window.addEventListener('openComplaintModal', handleOpenComplaintModal);
		return () => window.removeEventListener('openComplaintModal', handleOpenComplaintModal);
	}, []);

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50">
				<div className="text-gray-500">Проверка аутентификации...</div>
			</div>
		);
	}

	if (!isAuth) {
		navigate('/recruiter/login');
		return null;
	}

	let headerTitle: string | undefined = undefined;
	let headerIcon: React.ReactNode = undefined;
	let showMainTabs = true;
	let showPageHeader = true;

	if (isHhResponsesPage) {
		headerTitle = 'Отклики HH';
		showMainTabs = false;
	} else if (isAtsRoute) {
		showPageHeader = false;
		showMainTabs = false;
	} else if (path.startsWith('/recruiter/account')) {
		headerTitle = 'Личная информация';
		showMainTabs = false;
	} else if (path.startsWith('/recruiter/control')) {
		headerTitle = 'Управление';
		showMainTabs = false;
	} else if (path.startsWith('/recruiter/limits')) {
		headerTitle = 'Статистика и лимиты';
	} else if (path === '/recruiter/vacancy/create') {
		headerTitle = 'Новая вакансия';
		headerIcon = (
			<img src={portfolioIcon} alt="" className="w-5 h-5" />
		);
	}

	return (
		<div
			className="flex h-screen bg-[#F5F6F1] w-full font-inter"
			style={{
				['--brand-primary' as any]: colors.primary,
				['--brand-primary-hover' as any]: colors.primaryHover,
			}}
		>
			{/* Sidebar */}
			<Sidebar
				isCollapsed={isCollapsed}
				onToggle={toggle}
				onUserProfileClick={() => navigate('/recruiter/control?tab=personal')}
			/>

			<div className="min-h-0 min-w-0 flex-1 flex flex-col overflow-hidden">
				{showPageHeader && (
					<PageHeader
						title={headerTitle}
						icon={headerIcon}
						isSidebarCollapsed={isCollapsed}
						onSidebarToggle={toggle}
						onMenuClick={() => console.log('Menu clicked')}
					/>
				)}

				{showMainTabs && <MainTabNavigation />}

				<div ref={scrollContainerRef} className={`min-h-0 flex-1 custom-scrollbar relative ${isAtsRoute ? 'overflow-hidden' : 'overflow-y-auto'}`}>
					<Outlet />
				</div>
			</div>

			{/* Complaint Modal */}
			<ComplaintModal isOpen={isComplaintOpen} onClose={() => setComplaintOpen(false)} />
		</div>
	);
};

export default Layout;
